import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';
import { MerchantCheckoutSettings } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { LandingSettingsForm } from './form/form';
import { LABELS } from './keys';
import CheckoutPagePreview from './preview';
import { Controls, FormContainer, PanelContent, PreviewContainer, SectionHeader, Separator } from './styles';

interface GeneralSectionProps {
  account: MerchantCheckoutSettings;
  merchantUuid: string;
  isFormChanged: boolean;
  isSaveAvailable: boolean;
  resetFormValues: () => void;
  saveFormValues: () => void;
  onFormValueChange: (key: string, value: any) => void;
  isUpdating: boolean;
  error?: Record<string, string>;
  readOnlyMode: boolean;
  titleProps: {
    titleVisible: boolean;
    toggleTitleVisible: () => void;
  };
  hasPreview: boolean;
  allowToCustomizeTitle?: boolean;
}

export const CheckoutSection = ({
  account,
  merchantUuid,
  isSaveAvailable,
  isFormChanged,
  resetFormValues,
  onFormValueChange,
  saveFormValues,
  isUpdating,
  error,
  readOnlyMode,
  titleProps,
  hasPreview,
  allowToCustomizeTitle,
}: GeneralSectionProps) => {
  const { t } = useTranslation();

  return (
    <Panel
      label={t(LABELS.PANEL_TITLE)}
      controls={
        readOnlyMode ? undefined : (
          <Controls>
            <OptionallyVisible visible={isFormChanged}>
              <Button onClick={resetFormValues} secondary disabled={isUpdating}>
                {t(LABELS.CANCEL_BUTTON_LABEL)}
              </Button>
            </OptionallyVisible>
            <Button onClick={saveFormValues} primary disabled={!isSaveAvailable || isUpdating}>
              {t(LABELS.SAVE_BUTTON_LABEL)}
            </Button>
          </Controls>
        )
      }
    >
      <PanelContent>
        <FormContainer>
          <LandingSettingsForm
            titleProps={titleProps}
            account={account}
            onFieldValueChange={onFormValueChange}
            isUpdating={isUpdating}
            error={error}
            readOnlyMode={readOnlyMode}
            allowToCustomizeTitle={allowToCustomizeTitle}
          />
        </FormContainer>
      </PanelContent>
      <OptionallyVisible visible={hasPreview}>
        <Separator />
        <PreviewContainer>
          <SectionHeader>{t(LABELS.PREVIEW_SECTION_TITLE)}</SectionHeader>
          <OptionallyVisible visible={isUpdating}>
            <LoadingIndicator padded />
          </OptionallyVisible>
          <OptionallyVisible visible={!isUpdating}>
            <CheckoutPagePreview merchantUuid={merchantUuid} />
          </OptionallyVisible>
        </PreviewContainer>
      </OptionallyVisible>
    </Panel>
  );
};
